/**
 * Guest (auto-generated) email support for the dealer portal.
 *
 * KPRE AP dealers activate SIMs for walk-in customers who often have no email
 * address to hand. The guest flow gives them a throw-away address so the
 * activation can be completed on the spot — the customer changes it later from
 * the self-serve portal.
 *
 * The generated local part deliberately stays inside EMAIL_REGEX's allowed
 * character set (leading alphanumeric, no dots) so the address passes the same
 * validation a typed one does.
 */
import { EMAIL_REGEX } from '@apps/utils/constants'

// Digits + uppercase letters, matching the `user09SI8235X@telus.com` shape in
// the design. Ambiguous glyphs are kept: the address is never read aloud or
// re-typed by hand, and dropping them would shrink the keyspace for no gain.
const TOKEN_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

// Defaults, used until Contentful supplies an override and whenever the one it
// supplies is unusable. See GUEST_EMAIL_CONTENTFUL_SLUGS.
export const GUEST_EMAIL_PREFIX = 'user'
export const GUEST_EMAIL_DOMAIN = 'telus.com'
export const GUEST_EMAIL_TOKEN_LENGTH = 10

/**
 * Slugs the guest flow reads as `shortTextFields` on the Contentful `landingPage`
 * entry: the on/off switch, and the prefix and domain of the generated address.
 * Changing a value in the CMS takes effect on the next content fetch, with no
 * release — renaming a slug does not, it silently drops back to the defaults.
 */
export const GUEST_EMAIL_CONTENTFUL_SLUGS = Object.freeze({
  enabled: 'enableDealerGuestEmail',
  prefix: 'auto-generated-email-prefix',
  domain: 'auto-generated-email-domain'
})

/**
 * Interprets the published `enableDealerGuestEmail` value. Short text is a
 * string, so the switch is the text "false" (any case, surrounding whitespace
 * ignored); blank or unpublished leaves the guest flow on.
 */
export const isGuestEmailEnabled = (value) =>
  typeof value !== 'string' || value.trim().toLowerCase() !== 'false'

// Legal prefix: EMAIL_REGEX's local part minus the dot-separated labels, since
// the random token is appended directly to whatever the prefix ends with.
const GUEST_EMAIL_PREFIX_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9+_-]*$/

// Largest multiple of the alphabet length that fits in a byte. Bytes at or
// above it are discarded so every character stays equally likely (36 does not
// divide 256, so a plain modulo would bias the first 30 characters).
const REJECTION_LIMIT = Math.floor(256 / TOKEN_ALPHABET.length) * TOKEN_ALPHABET.length

const getCrypto = () => (typeof window !== 'undefined' ? window.crypto : undefined)

/**
 * Random token of `length` characters drawn from TOKEN_ALPHABET.
 * Uses the Web Crypto API where available and falls back to Math.random when it
 * is not (server render, older browsers). The token is a collision guard on a
 * throw-away address, never a secret, so the fallback is safe.
 */
export const generateGuestEmailToken = (length = GUEST_EMAIL_TOKEN_LENGTH) => {
  const size = Number.isInteger(length) && length > 0 ? length : GUEST_EMAIL_TOKEN_LENGTH
  const cryptoRef = getCrypto()
  let token = ''

  if (cryptoRef?.getRandomValues) {
    const buffer = new Uint8Array(size)
    while (token.length < size) {
      cryptoRef.getRandomValues(buffer)
      for (let i = 0; i < buffer.length && token.length < size; i += 1) {
        if (buffer[i] < REJECTION_LIMIT) {
          token += TOKEN_ALPHABET[buffer[i] % TOKEN_ALPHABET.length]
        }
      }
    }
    return token
  }

  while (token.length < size) {
    token += TOKEN_ALPHABET[Math.floor(Math.random() * TOKEN_ALPHABET.length)]
  }
  return token
}

// Both overrides are typed by a content editor, so they arrive with whatever the
// CMS accepted: stray whitespace, a leading `@`, a trailing dot, characters our
// own EMAIL_REGEX rejects. The generated address lands in a read-only field, so
// an invalid one would block the activation outright with no way for the rep to
// correct it — the resolvers below tidy up what they safely can and fall back to
// the default when what is left still cannot pass validation.

/**
 * Normalises a Contentful-supplied prefix, falling back to GUEST_EMAIL_PREFIX.
 * Whitespace is trimmed; anything outside GUEST_EMAIL_PREFIX_PATTERN is refused.
 */
export const resolveGuestEmailPrefix = (prefix) => {
  const trimmed = typeof prefix === 'string' ? prefix.trim() : ''

  return GUEST_EMAIL_PREFIX_PATTERN.test(trimmed) ? trimmed : GUEST_EMAIL_PREFIX
}

/**
 * Normalises a Contentful-supplied domain, falling back to GUEST_EMAIL_DOMAIN.
 * Whitespace, a leading `@` and a trailing dot are tidied away before validating.
 */
export const resolveGuestEmailDomain = (domain) => {
  const trimmed = typeof domain === 'string'
    ? domain.trim().replace(/^@+/, '').replace(/\.+$/, '').toLowerCase()
    : ''

  // Validated by running a probe address through EMAIL_REGEX rather than by a
  // second copy of its domain sub-pattern, so the two cannot drift apart. Note
  // EMAIL_REGEX rejects digits in domain labels, so `koodo2.com` is refused here
  // exactly as it would be if a rep typed it.
  return trimmed && EMAIL_REGEX.test(`${GUEST_EMAIL_PREFIX}@${trimmed}`)
    ? trimmed
    : GUEST_EMAIL_DOMAIN
}

/**
 * Builds a throw-away address such as `user09SI8235X@telus.com`.
 * Prefix and domain come from Contentful so they can be retuned without a
 * release; each falls back to its default independently when blank or unusable.
 */
export const generateGuestEmail = ({ prefix, domain, tokenLength } = {}) => {
  const safePrefix = resolveGuestEmailPrefix(prefix)
  const safeDomain = resolveGuestEmailDomain(domain)

  // Both halves are validated above and the token is drawn from TOKEN_ALPHABET,
  // so the result always satisfies EMAIL_REGEX.
  return `${safePrefix}${generateGuestEmailToken(tokenLength)}@${safeDomain}`
}

/**
 * Copy fallbacks for the guest flow, used only until the matching slugs are
 * published on the Contentful `landingPage` entry. Contentful always wins when
 * a slug resolves — delete this map once the entries are live.
 */
export const GUEST_EMAIL_FALLBACK_COPY = Object.freeze({
  en: {
    generating: 'Generating..',
    header: "Customer's Email Address",
    info: "Enter the email address to send the activation code to the customer. Select the auto-generated email option if customer doesn't have an email address.",
    infoHighlight: 'Customers can change their email address after registration.',
    customerOption: "Use Customer's email",
    autoGeneratedOption: 'Use temporary auto-generated email'
  },
  fr: {
    generating: 'Génération...',
    header: 'Adresse courriel du client',
    info: "Entrez l'adresse courriel à laquelle envoyer le code d'activation au client. Sélectionnez l'option de courriel généré automatiquement si le client n'a pas d'adresse courriel.",
    infoHighlight: "Les clients peuvent modifier leur adresse courriel après l'inscription.",
    customerOption: 'Utiliser le courriel du client',
    autoGeneratedOption: 'Utiliser un courriel temporaire généré automatiquement'
  }
})

export const getGuestEmailFallbackCopy = (language) =>
  GUEST_EMAIL_FALLBACK_COPY[language] || GUEST_EMAIL_FALLBACK_COPY.en

export default generateGuestEmail
